import { z } from 'zod';

import {
    TASK_NAVIGATION_SECTION,
    type TaskNavigationSection,
} from '../constants/task-navigation-sections.js';

const TASK_NAVIGATION_SECTION_VALUES = Object.values(TASK_NAVIGATION_SECTION) as [
    TaskNavigationSection,
    ...TaskNavigationSection[],
];

export const TaskNavigationSectionSchema = z.enum(TASK_NAVIGATION_SECTION_VALUES);

// ─── Task Progress Types ────────────────────────────────────────────
// Shared between BFF (task init), workers (subtask updates), and
// frontend (sidebar polling + card rendering).

export const TaskSubtaskSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    /** Completion ratio: 0 = not started, 0–1 = in progress, 1 = done */
    progress: z.number().min(0).max(1),
    /** Whether this subtask has encountered errors (orthogonal to progress) */
    hasErrors: z.boolean().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export type TaskSubtask = z.infer<typeof TaskSubtaskSchema>;

export const TaskStageSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    subtasks: z.array(TaskSubtaskSchema).min(1),
});
export type TaskStage = z.infer<typeof TaskStageSchema>;

export const TaskNavigationSchema = z.object({
    tenantId: z.string().min(1),
    page: z.string().min(1),
    params: z.record(z.string(), z.string()).optional(),
    itemIds: z.array(z.string()).optional(),
    /** Region within the page to scroll to — a `TASK_NAVIGATION_SECTION` key. */
    section: TaskNavigationSectionSchema.optional(),
});
export type TaskNavigation = z.infer<typeof TaskNavigationSchema>;

export const TaskRecordSchema = z.object({
    correlationId: z.string().uuid(),
    userEmail: z.string().email(),
    tenantId: z.string().min(1),
    type: z.string().min(1),
    title: z.string().min(1),
    createdAt: z.string().datetime(),
    stages: z.array(TaskStageSchema).min(1),
    navigation: TaskNavigationSchema.optional(),
    cancellable: z.boolean().optional(),
});
export type TaskRecord = z.infer<typeof TaskRecordSchema>;

export const TaskListResponseSchema = z.array(TaskRecordSchema);
export type TaskListResponse = z.infer<typeof TaskListResponseSchema>;
